import { Box, Typography, styled } from '@mui/material';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Confetti from 'react-confetti';
import { t } from 'i18next';
import { useEffect } from 'react';
import dayjs from 'dayjs';
import StyledButton from '../../../components/Common/StyledButton';
import { miniwebActions } from '../../../redux/miniweb/miniwebSlice';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { COACH_PROFILE_ROUTE } from '../../../utils/routes';
import MiniWebSiteHeader from '../MiniWebSiteHeader/MiniWebSiteHeader';

const StyledTypography = styled(Typography)({
  background: 'linear-gradient(138deg, #6599A5 24.11%, #3B667E 83.2%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  textFillColor: 'transparent',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  textTransform: 'capitalize',
});

const BookingConfirmation = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { username } = useParams();
  const { bookingData } = useAppSelector((state) => state.miniweb);
  useEffect(() => {
    dispatch(miniwebActions.createCoachMiniwebBookingReset());
  }, []);

  const handleDismiss = () => {
    if (username) {
      navigate(COACH_PROFILE_ROUTE.replace(':username', username));
    }
  };

  useEffect(() => {
    // Prevent the back button from navigating back to the form
    window.history.pushState(null, '', window.location.href);
    // Redirect to the coach profile page if the user tries to go back
    function handlePopState() {
      // Redirect to the coach profile page if the user tries to go back
      window.location.replace(`/u/${username}`);
    }
    window.onpopstate = handlePopState;

    return () => {
      window.onpopstate = null;
    };
  }, []);

  useEffect(() => {
    if (!bookingData.id) {
      if (username) {
        navigate(COACH_PROFILE_ROUTE.replace(':username', username));
        return;
      }
      navigate(COACH_PROFILE_ROUTE);
    }
  }, [bookingData, navigate, username]);

  return (
    <Box
      height="100vh"
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="100%"
      borderRadius="10px"
      sx={{
        overflowY: 'auto',
        background: 'linear-gradient(163deg, #FFF 10.13%, #FFF4F0 89.18%)',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
      }}
    >
      <Confetti width={window.innerWidth - 1} height={window.innerHeight - 1} recycle={false} />
      <MiniWebSiteHeader />
      <Box
        zIndex={2}
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        height="600px"
        width="365px"
        borderRadius="10px"
        border="1px solid #FFDDC8"
        gap="10px"
        sx={{
          background: 'rgba(255, 255, 255, 0.8)',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        }}
      >
        <Box textAlign="center">
          <img src="/assets/icons/confetti-red.svg" alt="bookings" width={56} height={56} />
          <Typography fontSize="22px" fontWeight={700}>
            {t('bookings.bookingConfirmed')}
          </Typography>
        </Box>
        <Box
          textAlign="center"
          sx={{ backgroundColor: '#7CAEBE1A' }}
          borderRadius="10px"
          display="flex"
          width="85%"
          p="16px"
          gap={1}
          mt={5}
          flexDirection="column"
        >
          <StyledTypography fontSize="20px" fontWeight={700}>
            {bookingData.servicePackage?.title}
          </StyledTypography>
          <Box display="flex" flexDirection="row" justifyContent="center" width="100%">
            <StyledTypography fontSize="17px" fontWeight={700} px={1}>
              {dayjs(bookingData.date).format('YYYY-MM-DD')}
            </StyledTypography>
            <StyledTypography fontSize="17px" fontWeight={700}>
              {' | '}
            </StyledTypography>
            <StyledTypography fontSize="17px" fontWeight={700} px={1}>
              {' '}
              {dayjs(bookingData.date).format('hh:mm A')}
            </StyledTypography>
          </Box>
          <StyledTypography fontSize="17px" fontWeight={700}>
            {t('bookings.bookingId')} : {bookingData.id}
          </StyledTypography>
        </Box>
        <Box width="85%">
          <Typography textAlign="center">{t('bookings.bookingConfirmationDesc')}</Typography>
        </Box>
        <Box pt={2} display="flex" flexDirection="column" alignItems="center" width="82%" gap={2}>
          <Typography fontWeight={700}>{t('bookings.getApp')}</Typography>
          <Box display="flex" flexDirection="row" justifyContent="space-between" width="100%">
            <Link to="https://apps.apple.com/lk/app/progress-magic/id6469035694" target="_blank">
              <img
                src="/assets/images/appstore.png"
                alt="appstore-icon"
                width="133px"
                height="44px"
              />
            </Link>
            <Link
              to="https://play.google.com/store/apps/details?id=com.progressmagic"
              target="_blank"
            >
              <img
                src="/assets/images/playstore.png"
                alt="playstore-icon"
                width="135px"
                height="44px"
              />
            </Link>
          </Box>
        </Box>
        <Box pt={4}>
          <StyledButton onClick={handleDismiss}>{t('common.dismiss')}</StyledButton>
        </Box>
      </Box>
      <Box position="fixed" bottom={5} zIndex={1} alignItems="center" width="100%">
        <Box display="flex" flexDirection="column" textAlign="center">
          <Typography variant="caption2Regular">
            2024 Progress Magic. All rights reserved
          </Typography>
          <Typography variant="caption2Bold">Privacy Policy</Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default BookingConfirmation;
